import { Sparkles, Heart, Check } from 'lucide-react';
import type { DayStats } from '../../lib/weekStats';

interface WeekStripProps {
  stats: readonly DayStats[];
  todayKey: string;
  selectedKey: string;
  onSelect: (date: Date) => void;
}

const DOW_SHORT: Record<DayStats['dayOfWeek'], string> = {
  mon: 'Mo',
  tue: 'Di',
  wed: 'Mi',
  thu: 'Do',
  fri: 'Fr',
  sat: 'Sa',
  sun: 'So',
};

export function WeekStrip({
  stats,
  todayKey,
  selectedKey,
  onSelect,
}: WeekStripProps): JSX.Element {
  return (
    <ol className="grid grid-cols-7 gap-1.5" aria-label="Tag waehlen">
      {stats.map((s) => {
        const isToday = s.dayKey === todayKey;
        const isSelected = s.dayKey === selectedKey;
        return (
          <li key={s.dayKey}>
            <DayPill stats={s} isToday={isToday} isSelected={isSelected} onSelect={onSelect} />
          </li>
        );
      })}
    </ol>
  );
}

function DayPill({
  stats,
  isToday,
  isSelected,
  onSelect,
}: {
  stats: DayStats;
  isToday: boolean;
  isSelected: boolean;
  onSelect: (date: Date) => void;
}): JSX.Element {
  const isFullTraining =
    stats.plan.kind === 'training' &&
    stats.totalExercises > 0 &&
    stats.completedExercises === stats.totalExercises;
  const ariaLabel = labelFor(stats, isToday, isSelected);

  // Container-Stile
  const base =
    'flex h-20 w-full flex-col items-center justify-center rounded-xl border text-center transition-transform duration-[120ms] active:scale-[0.97] focus-visible:outline-2 focus-visible:outline-emerald-500 focus-visible:outline-offset-2';
  const fillClass = isSelected
    ? 'border-emerald-600 bg-emerald-500 text-white shadow-sm'
    : isToday
      ? 'border-emerald-500 bg-white text-zinc-900'
      : 'border-zinc-200 bg-white text-zinc-900';

  return (
    <button
      type="button"
      onClick={() => onSelect(stats.date)}
      aria-current={isSelected ? 'date' : undefined}
      aria-label={ariaLabel}
      className={`${base} ${fillClass}`}
    >
      <span
        className={`text-[11px] font-semibold uppercase ${
          isSelected ? 'text-emerald-50' : 'text-zinc-500'
        }`}
      >
        {DOW_SHORT[stats.dayOfWeek]}
      </span>
      <span className={`text-base font-semibold tabular-nums ${isSelected ? 'text-white' : ''}`}>
        {stats.date.getDate()}
      </span>
      <span className="mt-0.5 inline-flex h-4 items-center justify-center">
        {renderStatus(stats, isFullTraining, isSelected)}
      </span>
    </button>
  );
}

function renderStatus(
  s: DayStats,
  isFullTraining: boolean,
  isSelected: boolean,
): JSX.Element {
  if (s.plan.kind === 'pilates') {
    return (
      <Sparkles
        aria-hidden="true"
        className={`size-3.5 ${isSelected ? 'text-amber-100' : 'text-amber-500'}`}
      />
    );
  }
  if (s.plan.kind === 'recovery') {
    return (
      <Heart
        aria-hidden="true"
        className={`size-3.5 ${isSelected ? 'text-emerald-100' : 'text-emerald-500'}`}
      />
    );
  }
  if (isFullTraining) {
    return (
      <Check
        aria-hidden="true"
        className={`size-3.5 ${isSelected ? 'text-white' : 'text-emerald-600'}`}
      />
    );
  }
  if (s.totalExercises > 0 && s.completedExercises > 0) {
    return (
      <span
        className={`text-[10px] font-semibold tabular-nums ${
          isSelected ? 'text-emerald-50' : 'text-zinc-500'
        }`}
      >
        {s.completedExercises}/{s.totalExercises}
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      className={`size-1.5 rounded-full ${isSelected ? 'bg-emerald-100' : 'bg-zinc-200'}`}
    />
  );
}

function labelFor(s: DayStats, isToday: boolean, isSelected: boolean): string {
  const base = `${DOW_SHORT[s.dayOfWeek]} ${s.date.getDate()}`;
  let suffix: string;
  if (s.plan.kind === 'pilates') suffix = 'Pilates';
  else if (s.plan.kind === 'recovery') suffix = 'Recovery';
  else suffix = `${s.completedExercises} von ${s.totalExercises} Uebungen`;
  const markers = [isToday ? 'Heute' : null, isSelected ? 'ausgewaehlt' : null]
    .filter(Boolean)
    .join(', ');
  return markers ? `${base} — ${suffix} (${markers})` : `${base} — ${suffix}`;
}
